import React, { useState, useEffect } from 'react';
import './Schedule.css';
import Cookies from 'js-cookie';

const Schedule = ({ initialValues, trackingMode }) => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [finishDate, setFinishDate] = useState(Cookies.get('finishDate') || '');
  const [manualTarget, setManualTarget] = useState(Cookies.get('manualTarget') === 'true' || false);
  const [targetPoints, setTargetPoints] = useState(Cookies.get('targetPoints') || '');
  const [targetSolved, setTargetSolved] = useState(Cookies.get('targetSolved') || '');
  const [displayPoints, setDisplayPoints] = useState('');
  const [displaySolved, setDisplaySolved] = useState('');
  const [trackIncrement, setTrackIncrement] = useState(0);
  const [solvedIncrement, setSolvedIncrement] = useState(0);

  const isProgamsMode = trackingMode === 'solved' && initialValues.solved > 0;

  const [initialValuesState, setInitialValues] = useState({
    codeTrack: '',
    dt: '',
    dc: '',
    points: '',
    requiredPoints: '',
    codeTest: '',
    solved: '',
    requiredSolved: '',
    codeTutor: ''
  });

  useEffect(() => {
    if (initialValues) {
      setInitialValues({
        ...initialValues,
        requiredPoints: initialValues.requiredPoints || '',
        requiredSolved: initialValues.requiredSolved || ''
      });

      if (!manualTarget) {
        if (isProgamsMode) {
          setManualTarget(
            (initialValues.requiredSolved === 0 || (initialValues.solved >= (initialValues.requiredSolved || 0)))
          );
          setTargetSolved(
            (initialValues.requiredSolved > 0 && initialValues.solved < initialValues.requiredSolved) ? initialValues.requiredSolved : ''
          );
          setDisplaySolved(initialValues.requiredSolved || 0);
        } else {
          setManualTarget(
            (initialValues.requiredPoints === 0 || (initialValues.points >= initialValues.requiredPoints))
          );
          setTargetPoints(
            (initialValues.requiredPoints > 0 && initialValues.points < initialValues.requiredPoints) ? initialValues.requiredPoints : ''
          );
          setDisplayPoints(initialValues.requiredPoints);
        }
      }
    }
  }, [initialValues, manualTarget, isProgamsMode]);

  useEffect(() => {
    if (isProgamsMode) {
      setDisplaySolved(
        manualTarget ? targetSolved : initialValuesState.requiredSolved
      );
    } else {
      setDisplayPoints(
        manualTarget ? targetPoints : initialValuesState.requiredPoints
      );
    }
  }, [targetPoints, targetSolved, manualTarget, initialValuesState.requiredPoints, initialValuesState.requiredSolved, isProgamsMode]);

  useEffect(() => {
    if (initialValues.deadline) {
      const currentDate = new Date();
      const deadlineDate = new Date(initialValues.deadline);
      if (isProgamsMode) {
        if (
          initialValues.solved < initialValues.requiredSolved &&
          currentDate < deadlineDate
        ) {
          setFinishDate(initialValues.deadline);
        }
      } else {
        if (
          initialValues.points < initialValues.requiredPoints &&
          currentDate < deadlineDate
        ) {
          setFinishDate(initialValues.deadline);
        }
      }
    }
  }, [initialValues.deadline, initialValues.points, initialValues.requiredPoints, initialValues.solved, initialValues.requiredSolved, isProgamsMode]);

  useEffect(() => {
    const savedFinishDate = Cookies.get('finishDate');
    if (savedFinishDate) {
      setFinishDate(savedFinishDate);
    }
  }, []);
  
  useEffect(() => {
    Cookies.set('finishDate', finishDate, {
      expires: 365,
      sameSite: 'Lax',
      secure: true
    });
  }, [finishDate]);

  useEffect(() => {
    const savedTargetPoints = Cookies.get('targetPoints');
    if (savedTargetPoints) {
      setTargetPoints(savedTargetPoints);
    }
  }, []);
  
  useEffect(() => {
    Cookies.set('targetPoints', targetPoints, {
      expires: 365,
      sameSite: 'Lax',
      secure: true
    });
  }, [targetPoints]);

  useEffect(() => {
    const savedTargetSolved = Cookies.get('targetSolved');
    if (savedTargetSolved) {
      setTargetSolved(savedTargetSolved);
    }
  }, []);
  
  useEffect(() => {
    Cookies.set('targetSolved', targetSolved, {
      expires: 365,
      sameSite: 'Lax',
      secure: true
    });
  }, [targetSolved]);

  useEffect(() => {
    Cookies.set('manualTarget', manualTarget, {
      expires: 365,
      sameSite: 'Lax',
      secure: true
    });
  }, [manualTarget]);

  const calculatePoints = (tracks, dt, dc, codeTest) => {
    return Math.floor(tracks) * 2 + Math.floor(dt) * 20 + Math.floor(dc) * 2 + Math.floor(codeTest) * 30;
  };

  const calculateSolved = (codeTrack, codeTutor, codeTest) => {
    return Math.floor(codeTrack) + Math.floor(codeTutor) + Math.floor(codeTest);
  };

  const getDifficulty = (increment) => {
    if (increment <= 10) return 'Easy';
    if (increment <= 25) return 'Medium';
    if (increment <= 50) return 'Hard';
    return 'Very Hard';
  };

  const getDifficultyColor = (increment) => {
    if (increment <= 10) return '#4CAF50';
    if (increment <= 25) return '#ffa500';
    return '#F44336';
  };

  const generateSchedule = () => {
    setLoading(true);
    setError('');

    const { codeTrack, dt, dc, points, codeTest, solved, codeTutor } = initialValuesState;

    if (!finishDate) {
      setError('Please enter the date!!');
      setLoading(false);
      return;
    }

    if (isProgamsMode) {
      if (manualTarget && !targetSolved) {
        setError('Please enter target programs count!!');
        setLoading(false);
        return;
      }
    } else {
      if (manualTarget && !targetPoints) {
        setError('Please enter target points!!');
        setLoading(false);
        return;
      }
    }

    let currentTracks = parseInt(codeTrack);
    let currentDt = parseInt(dt);
    let currentDc = parseInt(dc);
    let currentPoints = parseInt(points);
    let currentCodeTest = parseInt(codeTest);
    let currentCodeTutor = parseInt(codeTutor);
    
    // Use the actual solved count from the server, not calculated
    let currentSolved = parseInt(solved);

    const targetValue = isProgamsMode ? 
      (manualTarget ? (targetSolved || 0) : (initialValuesState.requiredSolved || 0)) :
      (manualTarget ? (targetPoints || 0) : (initialValuesState.requiredPoints || 0));

    const currentValue = isProgamsMode ? currentSolved : currentPoints;

    if (targetValue < 1) {
      setError(`Target ${isProgamsMode ? 'Programs' : 'Points'} must be greater than 0!`);
      setLoading(false);
      return;
    }
    
    if (targetValue > 99999) {
      setError(`Target ${isProgamsMode ? 'Programs' : 'Points'} cannot exceed 99999!`);
      setLoading(false);
      return;
    }

    if (targetValue <= currentValue) {
      setError(`Target ${isProgamsMode ? 'Programs' : 'Points'} must be greater than current ${isProgamsMode ? 'Programs' : 'Points'} ${currentValue}!!`);
      setLoading(false);
      return;
    }

    const today = new Date();
    const finish = new Date(finishDate);

    today.setHours(0, 0, 0, 0);
    finish.setHours(0, 0, 0, 0);

    const diffMilliseconds = finish - today;
    const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
    const daysToFinish = diffMilliseconds / oneDayInMilliseconds;

    if (daysToFinish <= 0) {
      setError('Date must be in the future!!');
      setLoading(false);
      return;
    }

    let newSchedule = [
      {
        date: today.toLocaleDateString('en-GB', { year: '2-digit', month: '2-digit', day: '2-digit' }),
        tracks: currentTracks,
        dt: currentDt,
        dc: currentDc,
        points: currentPoints,
        solved: currentSolved,
      },
    ];

    let currentDate = new Date(today);
    
    if (isProgamsMode) {
      // Program count mode
      let remainingSolved = targetValue - currentSolved;
      
      // Calculate the actual track increment needed (excluding DC/DT which are automatic)
      const trackIncrementForDifficulty = Math.ceil((remainingSolved - daysToFinish * 2) / daysToFinish);
      setSolvedIncrement(Math.max(0, trackIncrementForDifficulty));

      for (let i = 1; i <= daysToFinish; i++) {
        currentDate.setDate(currentDate.getDate() + 1);
        
        // Calculate how many programs we still need
        const currentProgramsNeeded = targetValue - currentSolved;
        const daysRemaining = daysToFinish - i + 1;
        
        // Calculate increment for this day (only for tracks, since DC/DT are fixed +1 each day)
        let trackIncrement = Math.ceil((currentProgramsNeeded - daysRemaining * 2) / daysRemaining); // -2 because DC+DT=2 programs per day
        
        // Ensure we don't go negative
        trackIncrement = Math.max(0, trackIncrement);
        
        // If this is the last day, calculate exact amount needed
        if (i === daysToFinish) {
          trackIncrement = currentProgramsNeeded - 2; // -2 for the DC+DT increment
          trackIncrement = Math.max(0, trackIncrement);
        }
        
        // Increment tracks by the calculated amount
        currentTracks += trackIncrement;
        
        // Also increment DT/DC
        currentDt += 1;
        currentDc += 1;
        
        // Total programs increase = track increment + DC increment + DT increment
        const totalProgramIncrease = trackIncrement + 1 + 1;
        currentSolved += totalProgramIncrease;
        
        // Recalculate points only
        currentPoints = calculatePoints(currentTracks, currentDt, currentDc, currentCodeTest);

        newSchedule.push({
          date: currentDate.toLocaleDateString('en-GB', { year: '2-digit', month: '2-digit', day: '2-digit' }),
          tracks: Math.floor(currentTracks),
          dt: currentDt,
          dc: currentDc,
          points: Math.floor(currentPoints),
          solved: Math.floor(currentSolved)
        });

        // Stop if we've reached the target
        if (currentSolved >= targetValue) break;
      }
    } else {
      // Points mode (existing logic)
      let remainingPoints = targetValue - currentPoints;
      const pointsFromDtDc = daysToFinish * (20 + 2);
      const remainingPointsForTracks = remainingPoints - pointsFromDtDc;
      const trackIncrement = Math.max(0, Math.ceil(remainingPointsForTracks / (2 * daysToFinish)));
      setTrackIncrement(trackIncrement);

      for (let i = 1; i <= daysToFinish; i++) {
        currentDate.setDate(currentDate.getDate() + 1);
        currentDt += 1;
        currentDc += 1;
        currentTracks += trackIncrement;

        currentPoints = calculatePoints(currentTracks, currentDt, currentDc, currentCodeTest);
        currentSolved = calculateSolved(currentTracks, currentCodeTutor, currentCodeTest);
        
        if (currentPoints >= targetValue) {
          const prevDt = currentDt - 1;
          const prevDc = currentDc - 1;
          const prevTracks = currentTracks - trackIncrement;
          let prevPoints = calculatePoints(prevTracks, prevDt, prevDc, currentCodeTest);
          
          let finalDt = prevDt;
          let finalDc = prevDc;
          let finalTracks = prevTracks;
          let finalPoints = prevPoints;
          
          const pointsNeeded = targetValue - prevPoints;

          if (pointsNeeded <= 2) {
            finalDc++;
            finalPoints = calculatePoints(finalTracks, finalDt, finalDc, currentCodeTest);
            
            if (finalPoints < targetValue) {
              finalDt++;
              finalPoints = calculatePoints(finalTracks, finalDt, finalDc, currentCodeTest);
              
              while (finalPoints < targetValue) {
                finalTracks++;
                finalPoints = calculatePoints(finalTracks, finalDt, finalDc, currentCodeTest);
              }
            }
          } else {
            finalDt++;
            finalPoints = calculatePoints(finalTracks, finalDt, finalDc, currentCodeTest);
            
            if (finalPoints < targetValue) {
              finalDc++;
              finalPoints = calculatePoints(finalTracks, finalDt, finalDc, currentCodeTest);
              
              while (finalPoints < targetValue) {
                finalTracks++;
                finalPoints = calculatePoints(finalTracks, finalDt, finalDc, currentCodeTest);
              }
            }
          }

          currentSolved = calculateSolved(Math.floor(finalTracks), currentCodeTutor, currentCodeTest);

          newSchedule.push({
            date: currentDate.toLocaleDateString('en-GB', { year: '2-digit', month: '2-digit', day: '2-digit' }),
            tracks: Math.floor(finalTracks),
            dt: finalDt,
            dc: finalDc,
            points: Math.floor(finalPoints),
            solved: Math.floor(currentSolved)
          });
          break;
        }

        newSchedule.push({
          date: currentDate.toLocaleDateString('en-GB', { year: '2-digit', month: '2-digit', day: '2-digit' }),
          tracks: Math.floor(currentTracks),
          dt: currentDt,
          dc: currentDc,
          points: Math.floor(currentPoints),
          solved: Math.floor(currentSolved)
        });
      }
    }

    setSchedule(newSchedule);
    setLoading(false);
  };

  if (loading) return <div className="loading">Generating schedule...</div>;

  return (
    <div className="schedule-container">
      <h2 className="schedule-title">
        Schedule for {isProgamsMode ? 
          (displaySolved === 0 ? "" : `${displaySolved} Programs`) : 
          (displayPoints === 0 ? "" : `${displayPoints} Points`)}
      </h2>
      <div className="form-container">
        <input
          type="date"
          value={finishDate}
          min={new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('en-CA')}
          onChange={(e) => setFinishDate(e.target.value)}
          className="input-field date-input"
        />
        <div className="manual-target-container">
          {((isProgamsMode && initialValues.solved > 0) ||
            (!isProgamsMode && initialValues.requiredPoints !== 0 && initialValues.points < initialValues.requiredPoints)) && (
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={manualTarget}
                  onChange={() => {
                    setManualTarget(!manualTarget);
                    if (!manualTarget) {
                      if (isProgamsMode) {
                        setTargetSolved(initialValuesState.requiredSolved > 0 ? initialValuesState.requiredSolved : (initialValues.solved + 100));
                      } else {
                        setTargetPoints(initialValuesState.requiredPoints > 0 ? initialValuesState.requiredPoints : '');
                      }
                    }
                  }}
                  style={{ marginBottom: '18px' }}
                />
                &nbsp;Set target {isProgamsMode ? 'Programs' : 'Points'}
              </label>
            </div>
          )}
          {manualTarget && (
            <div className="fade-in">
              <input
                type="number"
                value={isProgamsMode ? targetSolved : targetPoints}
                onChange={(e) => isProgamsMode ? setTargetSolved(e.target.value) : setTargetPoints(e.target.value)}
                placeholder={isProgamsMode ? 
                  `Programs > ${initialValues.solved}` : 
                  `Points > ${initialValues.points}`}
                className="input-field target-input"
              />
            </div>
          )}
        </div>
        <button onClick={generateSchedule} className="generate-button" style={{ marginTop: '10px'}} >Generate</button>
      </div>
      {error && <div className="error-message">{error}</div>}

      {schedule.length > 0 && (
        <>
          <div style={{ padding: '1px', textAlign: 'center' }}>
            <p><b>{isProgamsMode ? 'Programs' : 'Tracks'} / Day</b>: {Math.ceil(isProgamsMode ? solvedIncrement + 2 : trackIncrement)}</p>
            <p>
              <b>Difficulty</b>: 
              <span style={{ color: getDifficultyColor(isProgamsMode ? solvedIncrement + 2 : trackIncrement), fontWeight: 'bold', marginLeft: '5px' }}>
                {getDifficulty(isProgamsMode ? solvedIncrement + 2 : trackIncrement)}
              </span>
            </p>
          </div>
          <br />
          
          <div className="schedule-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Track</th>
                  <th>DC</th>
                  <th>DT</th>
                  {!isProgamsMode && <th>PTS</th>}
                  {isProgamsMode && <th>PRG</th>}
                </tr>
              </thead>
              <tbody>
                {schedule.map((day, index) => (
                  <tr key={index} className={index === schedule.length - 1 ? 'last-row' : ''}>
                    <td>{day.date}</td>
                    <td>{day.tracks}</td>
                    <td>{day.dc}</td>
                    <td>{day.dt}</td>
                    {!isProgamsMode && <td>{day.points}</td>}
                    {isProgamsMode && <td>{day.solved}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Schedule;
